import { useEffect, useRef } from "react";

/**
 * useServiceWorkerMessages — Listen for messages from the Service Worker.
 *
 * When a `push:click` message with `level === 'CRITICAL'` arrives, plays the
 * alert sound. Autoplay is permitted here because this event originates from a
 * user tap on a push notification (user gesture via notificationclick in SW).
 *
 * @param {(data: object) => void} onMessage - Callback for SW messages.
 */
export function useServiceWorkerMessages(onMessage) {
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;

    function handleMessage(event) {
      // Validate message source: only accept messages from the active service worker.
      // navigator.serviceWorker.controller may be null during SW activation — in that
      // case we skip the source check rather than dropping the first-boot message.
      if (
        event.source &&
        navigator.serviceWorker.controller &&
        event.source !== navigator.serviceWorker.controller
      ) {
        return;
      }

      const data = event.data;
      if (!data) return;

      // Deliver to caller
      onMessageRef.current?.(data);

      // Play alert sound for CRITICAL push clicks (autoplay allowed — user gesture)
      if (data.type === "push:click" && data.level === "CRITICAL") {
        try {
          const audio = new Audio("/sounds/alert-critical.mp3");
          audio.play().catch(() => {
            // Autoplay policy may still block in some contexts; ignore silently.
          });
        } catch {
          // Audio API not available
        }
      }
    }

    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, []);
}
